import { useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { CreditCard } from 'lucide-react';
import { useAuthActions } from '@/security/auth-provider';
import { useAuthStore } from '@/stores/auth.store';
import {
  getAdminRoleLabel,
  getManagedOrganizationLabel,
  isPlatformAdminRole,
} from '@/features/platform/admin-scope';
import { cn, pillClass } from '@/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

function DashboardIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function TenantsIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  );
}

function TemplatesIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.5a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25" />
    </svg>
  );
}

function AiUsageIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v18m0 0h16.5M3.75 21l4.5-6 3 2.25 5.25-8.25 3.75 3.75" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.75 1.03A8.966 8.966 0 0018 18.72m-12 0A9.094 9.094 0 012.25 19.75 8.966 8.966 0 016 18.72m12 0a5.995 5.995 0 00-12 0m12 0a5.995 5.995 0 00-12 0m6-9a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zm6 3.75a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zm-12 0a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
    </svg>
  );
}

const PLATFORM_ADMIN_NAV: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Organizations', to: '/tenants', icon: <TenantsIcon /> },
  { label: 'Billing', to: '/billing', icon: <CreditCard className="h-[18px] w-[18px]" /> },
  { label: 'Users', to: '/users', icon: <UsersIcon /> },
  { label: 'Templates', to: '/system-templates', icon: <TemplatesIcon /> },
  { label: 'AI Usage', to: '/ai-usage', icon: <AiUsageIcon /> },
];

const ORG_ADMIN_NAV: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Clients', to: '/tenants', icon: <TenantsIcon /> },
  { label: 'Billing', to: '/billing', icon: <CreditCard className="h-[18px] w-[18px]" /> },
  { label: 'Users', to: '/users', icon: <UsersIcon /> },
  { label: 'Templates', to: '/system-templates', icon: <TemplatesIcon /> },
  { label: 'AI Usage', to: '/ai-usage', icon: <AiUsageIcon /> },
];

export function AdminTopbar() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { signOut, selectScope } = useAuthActions();
  const navigate = useNavigate();
  const { userInfo, activeScope, availableScopes } = useAuthStore();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const isPlatformAdmin = isPlatformAdminRole(userInfo?.role);
  const navItems = isPlatformAdmin ? PLATFORM_ADMIN_NAV : ORG_ADMIN_NAV;
  const fallbackDisplayName = getAdminRoleLabel(userInfo?.role);
  const managedOrganizationsLabel = getManagedOrganizationLabel(userInfo?.role, { plural: true });
  const switchableScopes = availableScopes.filter((scope) => scope.scopeKey !== activeScope?.scopeKey);
  const initials = userInfo?.displayName
    ? userInfo.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const handleLogout = () => {
    signOut();
    navigate({ to: '/' });
  };

  const handleScopeSwitch = async (scopeKey: string) => {
    await selectScope(scopeKey);
    setMobileNavOpen(false);
    navigate({ to: '/dashboard' });
  };

  return (
    <header className="border-b border-[#2c2520] bg-[linear-gradient(180deg,#1c1916_0%,#231f1b_100%)] px-4 py-3 text-white lg:hidden">
      <div className="flex items-center gap-3">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <button className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-stone-100 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
              <span className="sr-only">Open navigation</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[88vw] max-w-[380px] border-[#2c2520] bg-[linear-gradient(180deg,#1c1916_0%,#231f1b_100%)] px-0 py-0 text-white sm:max-w-[380px]"
          >
            <SheetHeader className="border-b border-white/10 px-5 pb-4 pt-6 text-left">
              <SheetTitle className="text-[20px] font-bold tracking-tight text-white">Admin Menu</SheetTitle>
              <SheetDescription className="text-[13px] leading-6 text-stone-300">
                Navigate the platform and switch scopes.
              </SheetDescription>
            </SheetHeader>

            <div className="flex h-full flex-col overflow-y-auto px-4 py-4">
              <nav className="space-y-2">
                {navItems.map((item) => {
                  const isActive = currentPath === item.to || currentPath.startsWith(item.to + '/');
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileNavOpen(false)}
                      className={cn(
                        'flex items-center gap-3 rounded-2xl px-4 py-3 text-[14px] font-semibold transition-colors',
                        isActive
                          ? 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/20'
                          : 'text-stone-200 hover:bg-white/5',
                      )}
                    >
                      <span className={cn(isActive ? 'text-amber-300' : 'text-stone-300')}>{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="mt-5 rounded-[24px] border border-white/10 bg-white/5 px-4 py-4 shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-300">Active Scope</div>
                <div className="mt-2 text-[15px] font-semibold text-stone-100">
                  {activeScope?.label ?? getAdminRoleLabel(userInfo?.role)}
                </div>
                <div className="mt-1 text-[12px] text-stone-300">{userInfo?.email}</div>
                <div className={pillClass(isPlatformAdmin ? 'amber' : 'blue', 'sm', 'mt-3 uppercase tracking-[0.12em]')}>
                  {getAdminRoleLabel(userInfo?.role)}
                </div>
              </div>

              {switchableScopes.length > 0 ? (
                <div className="mt-5 rounded-[24px] border border-white/10 bg-white/5 px-4 py-4 shadow-sm">
                  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-300">Switch Scope</div>
                  <div className="mt-3 space-y-2">
                    {switchableScopes.map((scope) => (
                      <button
                        key={scope.scopeKey}
                        type="button"
                        onClick={() => {
                          void handleScopeSwitch(scope.scopeKey);
                        }}
                        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-left transition-colors hover:bg-white/10"
                      >
                        <span className="truncate text-[13px] font-semibold text-stone-100">{scope.label}</span>
                        <span className="shrink-0 text-[11px] uppercase tracking-[0.12em] text-stone-300">
                          {scope.role.replace('_', ' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </SheetContent>
        </Sheet>

        <Link to="/dashboard" className="flex min-w-0 flex-1 items-center gap-3 no-underline">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-500/20 text-[13px] font-bold text-amber-300 ring-1 ring-amber-400/20">
            CL
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">Admin Console</div>
            <div className="truncate text-[18px] font-bold tracking-tight text-white">Classifai</div>
          </div>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex shrink-0 items-center gap-2 rounded-full p-1 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-600 text-[11px] font-bold text-white shadow-sm">
                {initials}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-2xl border-slate-200 bg-white p-1.5 shadow-xl">
            <DropdownMenuLabel className="px-3 py-2">
              <div className="text-[13px] font-semibold text-slate-950">
                {userInfo?.displayName ?? fallbackDisplayName}
              </div>
              <div className="mt-0.5 text-[12px] font-normal text-slate-500">
                {userInfo?.email}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Active scope: {activeScope?.label ?? getAdminRoleLabel(userInfo?.role)}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Managing {managedOrganizationsLabel.toLowerCase()}
              </div>
            </DropdownMenuLabel>
            {switchableScopes.length > 0 ? (
              <>
                <DropdownMenuSeparator className="bg-slate-100" />
                <DropdownMenuLabel className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Switch scope
                </DropdownMenuLabel>
                {switchableScopes.map((scope) => (
                  <DropdownMenuItem
                    key={scope.scopeKey}
                    onClick={() => {
                      void handleScopeSwitch(scope.scopeKey);
                    }}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-700 focus:bg-slate-50"
                  >
                    <span className="truncate">{scope.label}</span>
                    <span className="shrink-0 text-[11px] uppercase tracking-[0.12em] text-slate-500">
                      {scope.role.replace('_', ' ')}
                    </span>
                  </DropdownMenuItem>
                ))}
              </>
            ) : null}
            <DropdownMenuSeparator className="bg-slate-100" />
            <DropdownMenuItem
              onClick={handleLogout}
              className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium text-rose-600 focus:bg-rose-50 focus:text-rose-700"
            >
              <LogoutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
